/**
 * Sadhana daily-completion helpers.
 *
 * Per-card, non-punitive tracking. Each of the 3 CraftPrint cards is tracked
 * independently: its own mark-done, its own kept-days count, its own progress
 * toward a 30-day practice. Streaks pause on a miss (a missed day is simply a
 * no-op) and never reset to zero.
 *
 * Storage:
 *   • Logged-in users  → backend (craftprints doc `completions` field).
 *   • Anonymous/preview → localStorage (this module).
 */

export const SADHANA_TARGET_DAYS = 30;

const LS_KEY = "icraftz.sadhana.completions.v1";

// Curated, book-agnostic coping antidotes (Upaya) offered as tappable chips.
// Deterministic + free — no LLM. A reader taps one to set their remedy.
export const UPAYA_PRESET_CHIPS = [
  "Make it smaller — just two minutes today",
  "Stack it right after something I already do",
  "Set a visible reminder where I'll see it",
  "Prepare everything the night before",
  "Forgive the miss, simply resume tomorrow",
  "Tell someone I'm practising this",
  "Remove one step of friction",
  "Do it at the same time each day",
];

function todayStr() {
  // Local calendar date (YYYY-MM-DD). Matches the reader's sense of "today".
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readAll() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function writeAll(obj) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(obj));
  } catch {
    /* quota / private-mode — silently ignore, tracking is best-effort */
  }
}

function keyFor(bookId, lang, ideaId) {
  return `${bookId}:${(lang || "en").toLowerCase()}:${ideaId}`;
}

function summarize(dates) {
  const uniq = Array.from(new Set(Array.isArray(dates) ? dates : [])).sort();
  return {
    days_kept: uniq.length,
    done_today: uniq.includes(todayStr()),
    last_done: uniq.length ? uniq[uniq.length - 1] : null,
    target: SADHANA_TARGET_DAYS,
  };
}

/** Read anon completions for a set of idea ids → { "<ideaId>": summary }. */
export function readAnonCompletions(bookId, lang, ideaIds) {
  const all = readAll();
  const out = {};
  for (const id of ideaIds || []) {
    const entry = all[keyFor(bookId, lang, id)];
    out[String(id)] = { idea_id: Number(id), ...summarize(entry) };
  }
  return out;
}

/** Toggle today's completion for one card. Returns the updated summary. */
export function toggleAnonCompletion(bookId, lang, ideaId, done) {
  const all = readAll();
  const k = keyFor(bookId, lang, ideaId);
  const set = new Set(Array.isArray(all[k]) ? all[k] : []);
  const today = todayStr();
  if (done) set.add(today);
  else set.delete(today);
  all[k] = Array.from(set).sort();
  writeAll(all);
  return { idea_id: Number(ideaId), ...summarize(all[k]) };
}
